import * as moment from 'moment';
import { DateTime as LuxonDateTime } from 'luxon';

/**
 * This method is to put hour in pattern [iso8601](https://en.wikipedia.org/wiki/ISO_8601)
 * input hour 5:00 output 05:00
 * ```ts
 * formatHourToIso8601("5:00")
 * ```
 */
export function formatHourToIso8601(hour: string) {
  return moment(hour, 'h:mm').format('HH:mm');
}

/**
 * This method is to remove hour and timezone from date
 * and to put date in pattern [iso8601](https://en.wikipedia.org/wiki/ISO_8601)
 * input hour 2020-04-03T03:00:00.000Z output 2020-04-03
 * A Date object keeps its local calendar day (unlike toISOString, which shifts it to UTC)
 * ```ts
 * removeHourAndTimezoneFromDate("2020-04-03T03:00:00.000Z")
 * removeHourAndTimezoneFromDate(new Date(2026, 0, 1)) // 2026-01-01
 * ```
 */
export function removeHourAndTimezoneFromDate(date: Date | string) {
  return moment(date).format('YYYY-MM-DD');
}

/**
 * This method is to put the am pm on a hour without date
 * input hour 08:00 output 08:00am
 * ```ts
 * formatHourToShowAmPm("08:00")
 * ```
 */
export function formatHourToShowAmPm(hour: string) {
  return moment(`2000-01-01 ${hour}`).format('h:mma');
}

/**
 * Returns the calendar day of an ISO date-time in the offset it was written with,
 * not in the browser's zone. Range bounds are saved in the pro's account zone,
 * so reading them in the browser's zone moves the day when the two zones differ.
 * input 2026-10-25T23:59:59.999-04:00 output 2026-10-25 (in any browser zone)
 * ```ts
 * getDayInOwnOffset("2026-10-25T23:59:59.999-04:00")
 * ```
 */
export function getDayInOwnOffset(isoDateTime: string) {
  return LuxonDateTime.fromISO(isoDateTime, { setZone: true }).toFormat('yyyy-MM-dd');
}

/**
 * Returns the local calendar day of a Date picked in a date picker.
 * toISOString() would give the UTC day, which is the previous day east of UTC.
 * ```ts
 * getLocalDay(new Date(2026, 9, 25))
 * ```
 */
export function getLocalDay(date: Date) {
  return LuxonDateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
}
